import { useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import Input from '../Input/Input'
import { InputTypes } from '../../../lib/enums'
import './Datalist.scss';


type DatalistOption = { value: string; label?: string }

type DatalistProps = {
  id: string
  label: string
  placeholder: string
  suggestions: Array<string | DatalistOption>
  value: string
  defaultValue: string
  onChange: (newValue: string) => void  
  isDisabled: boolean
  isSmall: boolean
  className: string
  maxLength: number
  required: boolean
  filterFn: (input: string, option: DatalistOption) => boolean
  closeOnSelect: boolean
}

function normalize(option: string | DatalistOption): DatalistOption {
  return typeof option === 'string' ? { value: option, label: option } : option
}

export default function Datalist({
  id,
  label,
  placeholder,
  suggestions,
  value,
  defaultValue,
  onChange,
  isDisabled,
  isSmall,
  className,
  maxLength,
  required = true,
  filterFn,
  closeOnSelect = true,
}: DatalistProps) {

  const NO_HIGHLIGHT = -1 as const;
  const FIRST_HIGHLIGHT = 0 as const;

  const Keys = {
    ArrowDown: 'ArrowDown',
    ArrowUp: 'ArrowUp',
    Home: 'Home',
    End: 'End',
    Enter: 'Enter',
    Escape: 'Escape',
  } as const;

  const isControlled = value != null
  const listboxId = `${id ?? 'datalist'}-listbox`
  const rootRef = useRef<HTMLDivElement>(null)
  
  const [isOpen, setIsOpen] = useState(false);
  const [internalValue, setInternalValue] = useState<string>(value ?? defaultValue ?? '')
  const [highlight, setHighlight] = useState<number>(NO_HIGHLIGHT)

  const items: DatalistOption[] = useMemo(
    () => suggestions.map(normalize),
    [suggestions]
  )

  const filtered = useMemo(() => {
    const v = internalValue.trim().toLowerCase()
    const f = filterFn ?? ((input: string, opt: DatalistOption) =>
      opt.label?.toLowerCase().includes(input) || opt.value.toLowerCase().includes(input)
    )
    const arr = v ? items.filter(opt => f(v, opt)) : items
    return arr
  }, [items, internalValue, filterFn])

  useEffect(() => {
    if (isControlled) setInternalValue(value!)
  }, [isControlled, value])

  useEffect(() => {
    if (!isOpen) return

    const hasItemsToHighlight = filtered.length > 0
    const highlightOutOfBounds = highlight < 0 || highlight >= filtered.length;

    if (!hasItemsToHighlight) {
      setHighlight(NO_HIGHLIGHT)
      return
    }  

    if (highlightOutOfBounds) {
      setHighlight(FIRST_HIGHLIGHT)
    }
  }, [isOpen, filtered, highlight])

  useEffect(() => {
    const handleMouseDownOutsideComponent = (e: MouseEvent) => {
      if (!rootRef.current) return
      if (!rootRef.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener('mousedown', handleMouseDownOutsideComponent)
    return () => document.removeEventListener('mousedown', handleMouseDownOutsideComponent)
  }, [])

  const commit = (newVal: string) => {
    if (!isControlled) setInternalValue(newVal)
    onChange?.(newVal)
  }

  const handleOnFocus = () => {    
    setIsOpen(true);
  }

  const handleInputChange = (newVal: string) => {
    commit(newVal);
    const componentHasFocus = rootRef.current?.contains(document.activeElement);    
    if (componentHasFocus) {
      setIsOpen(true);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      setIsOpen(false);
    }, 120);
  };

  const handleOptionMouseDown = (e: React.MouseEvent<HTMLLIElement>, value: string) => {
  e.preventDefault();
  commit(value);

  if (closeOnSelect) {
    setIsOpen(false);
  }
};


  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    const { key } = e;
    const hasOptions = filtered.length > 0;
    const isArrowKey = key === Keys.ArrowDown || key === Keys.ArrowUp;
    const shouldOpenOnArrow = !isOpen && isArrowKey;

    if (shouldOpenOnArrow) {
      setIsOpen(true);
      return;
    }

    if (!isOpen || !hasOptions) return;
  
    if (key === 'Escape') {
      setIsOpen(false);
      return;
    }

    if (key === Keys.ArrowDown) {
      e.preventDefault();
      setHighlight(h => (h === NO_HIGHLIGHT ? FIRST_HIGHLIGHT : (h + 1) % filtered.length));
      return;
    }

  if (key === Keys.ArrowUp) {
    e.preventDefault();
    setHighlight(h => (h === NO_HIGHLIGHT ? FIRST_HIGHLIGHT : (h - 1 + filtered.length) % filtered.length));
    return;
  }

  if (key === Keys.Home) {
    e.preventDefault();
    setHighlight(FIRST_HIGHLIGHT);
    return;
  }

  if (key === Keys.End) {
    e.preventDefault();
    setHighlight(filtered.length - 1);
    return;
  }

  const hasSelection = highlight !== NO_HIGHLIGHT;

  if (key === Keys.Enter) {
    if (!hasSelection) {
      setIsOpen(false);
      return;
    }

    const selected = filtered[highlight];
    commit(selected.value);
    if (closeOnSelect) setIsOpen(false);
  }
};  
  return (
    <div
      ref={rootRef}
      className={classNames('msds-datalist', className)}
      role="combobox"
      aria-expanded={isOpen}
      aria-owns={listboxId}
      aria-haspopup="listbox"
      onKeyDown={handleKeyDown}
      onFocus={handleOnFocus}
      onBlur={() => handleBlur()} 
    >
      <Input
        id={id}
        type={InputTypes.Text}
        label={label}
        placeholder={placeholder}
        initialValue={internalValue}
        isDisabled={isDisabled}
        isSmall={isSmall}
        maxLength={maxLength}
        required={required}
        onEventType="onChange"          
        handleDataChange={(val: string) => handleInputChange(val)}       
        className="msds-datalist__input"
      />

      {isOpen && filtered.length > 0 && (
        <ul id={listboxId} role="listbox" className="msds-datalist__list">
          {filtered.map((opt, idx) => {
            const isActive = idx === highlight
            return (
              <li
                key={`${opt.value}-${idx}`}
                role="option"
                aria-selected={isActive}
                className={classNames('msds-datalist__item', isActive && 'is-active')}                
                onMouseDown={(e) => handleOptionMouseDown(e, opt.value)}
                onMouseEnter={() => setHighlight(idx)}
              >
                {opt.label ?? opt.value}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
